// A timetable's trips as their calls, for a feed that gives delays against
// the timetable instead of times and names only some of a trip's calls
// (Sydney Trains' and Sydney Ferries': GtfsSystem.schedule, feed.ts
// onSchedule); for a feed that names a trip only by its id (TriMet's:
// GtfsSystem.fill, with the trips' routes and directions); and for a system
// with no feed at all, its trains placed by the timetable alone (Sounder's,
// the streetcars': scheduledTrips, and feed.ts scheduleFeed). Built with the network, for the service days around the build
// (yesterday's trips run past midnight); a city whose trip ids change daily
// rebuilds several times a day.

import { eachRow } from './csv.ts';
import type { GtfsCall, GtfsTrip } from './types.ts';

// A call as the timetable has it: station, arrival and departure in s after
// the service day's start (null where stop_times.txt leaves the time out:
// 00:00:00 is a time), its stop_sequence, and pass: 1 where the train only
// runs through.
export type ScheduleCall = readonly [station: string, arr: number | null, dep: number | null, seq: number, pass?: 1];

// Packed generations expose calls only on demand, never a tuple tree.
export interface PackedCalls {
  readonly ids: readonly string[];
  readonly calls: (id: string) => readonly ScheduleCall[] | undefined;
  readonly span: (id: string) => readonly [number, number];
  readonly destination: (id: string) => string | undefined;
}

export interface GtfsSchedule {
  readonly packed?: PackedCalls;
  // The timetable's time zone (agency_timezone), which makes a feed's
  // start_date a service day.
  readonly timeZone: string;
  // Trip id -> its calls: station, arrival and departure in s after its
  // service day's start (GTFS times, which pass 24:00).
  readonly trips: Readonly<Record<string, readonly ScheduleCall[]>>;
  // Trip id -> the service days it runs on, as each day's start (s since
  // epoch: noon less 12 hours, local time, as GTFS has it).
  readonly days: Readonly<Record<string, readonly number[]>>;
  // Trip id -> its route_id and direction_id (ScheduleOptions.routes).
  readonly routes?: Readonly<Record<string, string>>;
  readonly dirs?: Readonly<Record<string, 0 | 1>>;
}

// One formatter per time zone (building one costs about 30 µs).
const offsetFormats = new Map<string, Intl.DateTimeFormat>();

// The zone's offset from UTC at an instant, s ("GMT+10:00" is 36000).
function offsetS(timeZone: string, epochS: number): number {
  let format = offsetFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'longOffset' });
    offsetFormats.set(timeZone, format);
  }
  const name =
    format
      .formatToParts(new Date(epochS * 1000))
      .find((p: Readonly<Intl.DateTimeFormatPart>) => p.type === 'timeZoneName')?.value ?? '';
  const m = /GMT([+-])(\d{2}):(\d{2})/u.exec(name);
  if (!m) return 0;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 3600 + Number(m[3]) * 60);
}

// A service day's start in a time zone: local noon less 12 hours.
export function serviceDayStart(timeZone: string, y: number, m: number, d: number): number {
  const utcNoon = Date.UTC(y, m - 1, d, 12) / 1000;
  return utcNoon - offsetS(timeZone, utcNoon) - 12 * 3600;
}

// A local wall time in a time zone ("2026-03-08 00:30" in Chicago) as s
// since epoch, by the offset in effect then: not a GTFS time, which counts
// from its service day's start (above), but a clock's reading. A time the
// clocks skip (02:30 as they go forward) is read by the offset before the
// change, so it lands just after the gap (03:30); a time they pass twice
// (01:30 as they go back) is the first. Those are Temporal's "compatible"
// choices. A zone changes its clocks at most once a day either side.
export function localTimeS(
  timeZone: string,
  y: number,
  m: number,
  d: number,
  h: number,
  min: number,
  s: number,
): number {
  const wall = Date.UTC(y, m - 1, d, h, min, s) / 1000;
  const before = offsetS(timeZone, wall - 86_400);
  const after = offsetS(timeZone, wall + 86_400);
  const valid = [wall - before, wall - after].filter((t) => wall - offsetS(timeZone, t) === t);
  return valid.length > 0 ? Math.min(...valid) : wall - before;
}

// The local date n days from an instant: [y, m, d] and "YYYYMMDD".
function localDate(
  timeZone: string,
  epochS: number,
  n: number,
): { readonly ymd: readonly [number, number, number]; readonly key: string } {
  const local = new Date((epochS + offsetS(timeZone, epochS)) * 1000);
  const day = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + n));
  const ymd = [day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()] as const;
  return { ymd, key: day.toISOString().slice(0, 10).replaceAll('-', '') };
}

const bySeq = (a: { readonly seq: number }, b: { readonly seq: number }): number => a.seq - b.seq;

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

// "25:10:00" as s after the service day's start; '' (a stop between
// timepoints) is null.
export const scheduleSeconds = (t: string): number | null => {
  if (t === '') return null;
  const [h = 0, m = 0, s = 0] = t.split(':').map(Number);
  return h * 3600 + m * 60 + s;
};

// The earliest and latest times of a trip's calls (0 without any).
export function span(calls: readonly ScheduleCall[]): readonly [first: number, last: number] {
  let first = Infinity;
  let last = -Infinity;
  for (const [, arr, dep] of calls) {
    for (const t of [arr, dep]) {
      if (t === null) continue;
      first = Math.min(first, t);
      last = Math.max(last, t);
    }
  }
  return Number.isFinite(first) ? [first, last] : [0, 0];
}

// GTFS times pass 24:00 (a trip that starts late runs into the next day,
// some well past it), so a trip from a service day before yesterday can
// still be running. The days before yesterday are read this far back, and a
// trip's are kept while its last call, this late, isn't yet past.
const MAX_DAYS_BACK = 7;
const LATE_S = 6 * 3600;

export interface ScheduleOptions {
  // The timetable's time zone (agency_timezone): "Australia/Sydney".
  readonly timeZone: string;
  // A stop id's station ('': no station, left out).
  readonly station: (stopId: string) => string;
  // Whether a route is one the page draws.
  readonly revenue: (routeId: string) => boolean;
  // A trip that carries no passengers though its route is a line's (Sydney
  // Trains' "Empty Train"s): no calls, so the feed's trip is dropped.
  readonly empty?: (headsign: string) => boolean;
  // Keep each trip's route and direction, for a feed that doesn't name them
  // and for scheduledTrips. Default: no.
  readonly routes?: boolean;
}

// Days: yesterday (its trips run past midnight) to the day after tomorrow,
// and before yesterday for a trip whose times run on past now (48:10:00 on
// the day before yesterday is ten past midnight today; MAX_DAYS_BACK,
// LATE_S).
export function selectSchedule(
  files: ReadonlyMap<string, string>,
  options: ScheduleOptions,
  nowS = Date.now() / 1000,
): { readonly schedule: GtfsSchedule; readonly yesterday: number } {
  const { timeZone, revenue, empty } = options;
  const window = Array.from({ length: MAX_DAYS_BACK + 3 }, (_, i) => i - MAX_DAYS_BACK).map((n) => {
    const { ymd, key } = localDate(timeZone, nowS, n);
    const [y, m, d] = ymd;
    return {
      key,
      start: serviceDayStart(timeZone, y, m, d),
      weekday: WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? 'sunday',
      // Before yesterday: kept only for trips still running (below).
      old: n < -1,
      services: new Set<string>(),
    };
  });
  eachRow(files.get('calendar.txt') ?? '', (get) => {
    for (const w of window) {
      if (get('start_date') <= w.key && w.key <= get('end_date') && get(w.weekday) === '1')
        w.services.add(get('service_id'));
    }
  });
  eachRow(files.get('calendar_dates.txt') ?? '', (get) => {
    for (const w of window) {
      if (get('date') !== w.key) continue;
      if (get('exception_type') === '1') w.services.add(get('service_id'));
      else w.services.delete(get('service_id'));
    }
  });
  const days: Record<string, number[]> = {};
  const trips: Record<string, readonly ScheduleCall[]> = {};
  const routes: Record<string, string> = {};
  const dirs: Record<string, 0 | 1> = {};
  eachRow(files.get('trips.txt') ?? '', (get) => {
    if (!revenue(get('route_id'))) return;
    const on: number[] = [];
    for (const w of window) if (w.services.has(get('service_id'))) on.push(w.start);
    if (on.length === 0) return;
    days[get('trip_id')] = on;
    if (options.routes === true) {
      routes[get('trip_id')] = get('route_id');
      dirs[get('trip_id')] = get('direction_id') === '1' ? 1 : 0;
    }
    if (empty?.(get('trip_headsign')) === true) trips[get('trip_id')] = [];
  });
  return {
    schedule: { timeZone, trips, days, ...(options.routes === true ? { routes, dirs } : {}) },
    yesterday: window[MAX_DAYS_BACK - 1]?.start ?? 0,
  };
}

export function buildSchedule(
  files: ReadonlyMap<string, string>,
  options: ScheduleOptions,
  nowS = Date.now() / 1000,
): GtfsSchedule {
  const { schedule, yesterday } = selectSchedule(files, options, nowS);
  const { trips, days } = schedule;
  const { station } = options;
  const rows = new Map<string, { readonly seq: number; readonly call: ScheduleCall }[]>();
  eachRow(files.get('stop_times.txt') ?? '', (get) => {
    const trip = get('trip_id');
    if (days[trip] === undefined || trips[trip] !== undefined) return;
    const sid = station(get('stop_id'));
    if (sid === '') return;
    let list = rows.get(trip);
    if (!list) rows.set(trip, (list = []));
    const arr = scheduleSeconds(get('arrival_time'));
    const dep = scheduleSeconds(get('departure_time'));
    const seq = Number(get('stop_sequence'));
    const pass = get('pickup_type') === '1' && get('drop_off_type') === '1';
    list.push({ seq, call: pass ? [sid, arr, dep, seq, 1] : [sid, arr, dep, seq] });
  });
  for (const [trip, list] of rows)
    Reflect.set(
      trips,
      trip,
      list.toSorted(bySeq).map((r) => r.call),
    );
  return retainScheduleDays(schedule, yesterday, nowS);
}

export function retainScheduleDays(
  schedule: GtfsSchedule,
  yesterday: number,
  nowS: number,
  spans?: ReadonlyMap<string, readonly [number, number]>,
): GtfsSchedule {
  const { days, trips, routes, dirs } = schedule;
  // The days before yesterday a trip's still running on, by its last call.
  for (const [trip, on] of Object.entries(days)) {
    const [, last] = spans?.get(trip) ?? scheduleSpan(schedule, trip);
    const kept = on.filter((start) => start >= yesterday || start + last + LATE_S >= nowS);
    if (kept.length > 0) Reflect.set(days, trip, kept);
    else {
      Reflect.deleteProperty(days, trip);
      Reflect.deleteProperty(trips, trip);
      if (routes) Reflect.deleteProperty(routes, trip);
      if (dirs) Reflect.deleteProperty(dirs, trip);
    }
  }
  return schedule;
}

export function scheduleTripIds(schedule: GtfsSchedule): readonly string[] {
  return schedule.packed?.ids ?? Object.keys(schedule.trips);
}
export function scheduleCalls(schedule: GtfsSchedule, id: string): readonly ScheduleCall[] | undefined {
  return schedule.packed
    ? schedule.packed.calls(id)
    : Object.hasOwn(schedule.trips, id)
      ? schedule.trips[id]
      : undefined;
}
export function scheduleSpan(schedule: GtfsSchedule, id: string): readonly [number, number] {
  return schedule.packed?.span(id) ?? span(schedule.trips[id] ?? []);
}

// The service day whose run of a trip (its first and last times) is
// nearest now.
function nearestDay(days: readonly number[], [first, last]: readonly [number, number], nowS: number): number | null {
  let best: number | null = null;
  let gap = Infinity;
  for (const d of days) {
    const g = Math.max(0, d + first - nowS, nowS - (d + last));
    if (g < gap) {
      gap = g;
      best = d;
    }
  }
  return best;
}

// A timetable call for a feed's trip (GtfsSystem.schedule): the call, in s
// since epoch, and its stop_sequence, by which the feed may name it.
export interface ScheduledCall {
  readonly call: GtfsCall;
  readonly seq: number;
}

// A trip's calls on its service day: the feed's start_date ("YYYYMMDD")
// when it gives one, else the day nearest now (s since epoch). null: not in
// the timetable, or not on that day.
export function scheduleLookup(
  schedule: GtfsSchedule,
): (trip: { readonly tripId: string; readonly startDate: string }, nowS: number) => readonly ScheduledCall[] | null {
  const dates = new Map<string, number | null>();
  const dayOf = (date: string): number | null => {
    let start = dates.get(date);
    if (start === undefined) {
      const m = /^(\d{4})(\d{2})(\d{2})$/u.exec(date);
      start = m ? serviceDayStart(schedule.timeZone, Number(m[1]), Number(m[2]), Number(m[3])) : null;
      dates.set(date, start);
    }
    return start;
  };
  return ({ tripId, startDate }, nowS) => {
    // Own properties only: a trip id like `__proto__` isn't in the timetable.
    if (!Object.hasOwn(schedule.days, tripId)) return null;
    const calls = scheduleCalls(schedule, tripId);
    const days = schedule.days[tripId];
    if (!calls || !days) return null;
    if (calls.length === 0) return [];
    const given = startDate === '' ? null : dayOf(startDate);
    const best = given === null ? nearestDay(days, span(calls), nowS) : days.includes(given) ? given : null;
    if (best === null) return null;
    const on = callsOn(calls, best);
    return calls.map(([, , , seq], i): ScheduledCall => ({ call: on[i] ?? ['', 0, 0], seq }));
  };
}

// A trip's calls on the service day that starts at `start` (s since
// epoch), as GtfsCall has them: a time the timetable leaves out is 0.
export function callsOn(calls: readonly ScheduleCall[], start: number): GtfsCall[] {
  const at = (t: number | null): number => (t === null ? 0 : start + t);
  return calls.map(([sid, arr, dep, , pass]): GtfsCall =>
    pass === 1 ? [sid, at(arr), at(dep), 1] : [sid, at(arr), at(dep)],
  );
}

// How long before its first departure a trip placed by the timetable is
// listed (waiting at its first station), and after its last arrival.
const BEFORE_S = 10 * 60;
const AFTER_S = 60;

// The trips running now by the timetable alone (a schedule built with
// routes), for a system with no feed: `feed` prefixes their ids (as a feed's
// do, GtfsTrip.id), `line` is a route's line ('' for none).
export function scheduledTrips(
  schedule: GtfsSchedule,
  nowS: number,
  feed: string,
  line: (routeId: string) => string,
): GtfsTrip[] {
  if (schedule.packed) {
    const out: GtfsTrip[] = [];
    for (const tripId of schedule.packed.ids) {
      const lineId = line(schedule.routes?.[tripId] ?? '');
      const dest = schedule.packed.destination(tripId);
      if (lineId === '' || dest === undefined) continue;
      const [first, last] = schedule.packed.span(tripId);
      for (const d of schedule.days[tripId] ?? []) {
        if (nowS < d + first - BEFORE_S || nowS > d + last + AFTER_S) continue;
        out.push({
          id: `${feed}:${tripId}:${d}`,
          line: lineId,
          vid: '',
          dir: schedule.dirs?.[tripId] ?? null,
          dest,
          calls: callsOn(schedule.packed.calls(tripId) ?? [], d),
          complete: true,
          scheduled: true,
        });
      }
    }
    return out;
  }
  const out: GtfsTrip[] = [];
  let indexed = scheduledIndexes.get(schedule);
  if (!indexed) {
    indexed = Object.entries(schedule.trips).flatMap(([tripId, calls]: readonly [string, readonly ScheduleCall[]]) => {
      const dest = calls.at(-1)?.[0];
      if (dest === undefined) return [];
      const [first, last] = span(calls);
      return [{ tripId, calls, dest, first, last, days: schedule.days[tripId] ?? [] }];
    });
    scheduledIndexes.set(schedule, indexed);
  }
  for (const { tripId, calls, dest, first, last, days } of indexed) {
    const lineId = line(schedule.routes?.[tripId] ?? '');
    if (lineId === '' || dest === undefined) continue;
    for (const d of days) {
      if (nowS < d + first - BEFORE_S || nowS > d + last + AFTER_S) continue;
      out.push({
        id: `${feed}:${tripId}:${d}`,
        line: lineId,
        vid: '',
        dir: schedule.dirs?.[tripId] ?? null,
        dest,
        calls: callsOn(calls, d),
        complete: true,
        scheduled: true,
      });
    }
  }
  return out;
}

interface ScheduledIndex {
  readonly tripId: string;
  readonly calls: readonly ScheduleCall[];
  readonly dest: string;
  readonly first: number;
  readonly last: number;
  readonly days: readonly number[];
}

// A generation's trip spans are immutable. Keep them beside that generation
// so repeated timetable-only lookups do not rescan every call.
const scheduledIndexes = new WeakMap<GtfsSchedule, readonly ScheduledIndex[]>();

const isObject = (v: unknown): v is Readonly<Record<string, unknown>> => typeof v === 'object' && v !== null;
const isTime = (v: unknown): boolean => v === null || typeof v === 'number';
const isCall = (v: unknown): v is ScheduleCall =>
  Array.isArray(v) &&
  (v.length === 4 || (v.length === 5 && v[4] === 1)) &&
  typeof v[0] === 'string' &&
  isTime(v[1]) &&
  isTime(v[2]) &&
  typeof v[3] === 'number';

export function isSchedule(v: unknown): v is GtfsSchedule {
  return (
    isObject(v) &&
    typeof v['timeZone'] === 'string' &&
    isObject(v['trips']) &&
    Object.values(v['trips']).every((c) => Array.isArray(c) && c.every((x) => isCall(x))) &&
    isObject(v['days']) &&
    Object.values(v['days']).every((d) => Array.isArray(d) && d.every((x) => typeof x === 'number')) &&
    (v['routes'] === undefined ||
      (isObject(v['routes']) && Object.values(v['routes']).every((r) => typeof r === 'string'))) &&
    (v['dirs'] === undefined || (isObject(v['dirs']) && Object.values(v['dirs']).every((d) => d === 0 || d === 1)))
  );
}
